import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import TeamMembersSection from './TeamMembersSection';
import { devClickDebug } from '../../devClickDebug';
import {
  DashboardPageHeader,
  dashboardCardClass,
  dashboardBtnPrimary,
  dashboardInputClass,
  dashboardTextareaClass,
  dashboardLabelClass,
  dashboardBtnRowEdit,
  dashboardBtnRowDelete,
  dashboardBtnModalCancel,
  dashboardModalBackdrop,
  dashboardModalWrap,
  dashboardModalPanelMd,
  dashboardModalHeader,
  dashboardModalBody,
  dashboardModalFooter,
  dashboardModalCloseBtn,
} from './DashboardChrome';

const teamsToggleBtn =
  'flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition-all sm:px-4';

const TeamsSection = ({
  teams,
  showAddTeamMember,
  setShowAddTeamMember,
  teamMemberForm,
  setTeamMemberForm,
  saveTeamMember,
  deleteTeamMember,
  editTeamMember,
  handleTeamMemberFormChange,
  editingTeamMember,
  setEditingTeamMember,
  subcontractors = [],
  showAddSubcontractor,
  setShowAddSubcontractor,
  subcontractorForm,
  setSubcontractorForm,
  saveSubcontractor,
  deleteSubcontractor,
  editSubcontractor,
  handleSubcontractorFormChange,
  editingSubcontractor,
  setEditingSubcontractor,
  userPlan,
}) => {
  const [searchParams] = useSearchParams();
  const [teamsTab, setTeamsTab] = useState(() => {
    const t = searchParams.get('tab');
    if (t === 'subcontractors') return 'subcontractors';
    if (t === 'portal') return 'portal';
    return 'contractors';
  });

  const emptySubForm = () => ({
    name: '',
    company: '',
    trade: '',
    phone: '',
    email: '',
    notes: '',
  });

  return (
    <div className="mx-auto max-w-7xl space-y-8 pb-10">
      <DashboardPageHeader
        eyebrow="Dashboard"
        title="Teams"
        description={
          teamsTab === 'contractors'
            ? 'Your crew: roles and hourly rates for labour on invoices and projects.'
            : teamsTab === 'subcontractors'
              ? 'External partners you can tag on labour expenses to keep project costs clear.'
              : 'Manage portal access for team members who can view or collaborate on your projects.'
        }
        action={
          teamsTab === 'contractors' ? (
            <button
              type="button"
              onClick={() => {
                devClickDebug('TeamsSection: Add team member button click', {});
                setShowAddTeamMember(true);
              }}
              className={dashboardBtnPrimary}
            >
              Add team member
            </button>
          ) : teamsTab === 'subcontractors' ? (
            <button type="button" onClick={() => setShowAddSubcontractor(true)} className={dashboardBtnPrimary}>
              Add partner
            </button>
          ) : null
        }
      />

      <div className="flex max-w-xl rounded-2xl border border-slate-200/90 bg-slate-50/80 p-1 shadow-inner">
        <button
          type="button"
          onClick={() => setTeamsTab('contractors')}
          className={`${teamsToggleBtn} ${
            teamsTab === 'contractors'
              ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          Contractors
        </button>
        <button
          type="button"
          onClick={() => setTeamsTab('subcontractors')}
          className={`${teamsToggleBtn} ${
            teamsTab === 'subcontractors'
              ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          Subcontractors
        </button>
        <button
          type="button"
          onClick={() => setTeamsTab('portal')}
          className={`${teamsToggleBtn} ${
            teamsTab === 'portal'
              ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          Portal Members
        </button>
      </div>

      {teamsTab === 'portal' && (
        <TeamMembersSection userPlan={userPlan} />
      )}

      {teamsTab === 'contractors' && (
        <div className={dashboardCardClass}>
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <h3 className="text-base font-semibold text-slate-900">Team members</h3>
            <p className="mt-1 text-sm text-slate-500">People on your payroll or regular crew.</p>
          </div>
          <div className="p-6">
            {teams.length === 0 ? (
              <div className="py-8 text-center text-gray-500">
                <svg className="mx-auto mb-4 h-12 w-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
                  />
                </svg>
                <p>No team members yet</p>
                <p className="text-sm">Add your first team member to get started</p>
                <button type="button" onClick={() => setShowAddTeamMember(true)} className={`${dashboardBtnPrimary} mt-4`}>
                  Add first team member
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {teams.map((teamMember) => (
                  <div
                    key={teamMember.id}
                    className="rounded-2xl border border-slate-200/90 bg-white p-4 shadow-sm transition-all hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="mb-3 flex flex-wrap items-center gap-3">
                          <h4 className="text-xl font-semibold text-gray-900">{teamMember.name}</h4>
                          <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
                            {teamMember.role}
                          </span>
                        </div>
                        <div className="mb-2 space-y-0.5 text-sm text-gray-600">
                          {teamMember.phone && (
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                />
                              </svg>
                              <span className="font-medium">Phone:</span>
                              <span className="text-gray-900">{teamMember.phone}</span>
                            </div>
                          )}
                          {teamMember.email && (
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                />
                              </svg>
                              <span className="font-medium">Email:</span>
                              <span className="text-gray-900">{teamMember.email}</span>
                            </div>
                          )}
                          {teamMember.hourlyRate ? (
                            <div className="flex items-center gap-2">
                              <svg className="h-4 w-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                                />
                              </svg>
                              <span className="font-medium">Hourly Rate:</span>
                              <span className="text-gray-900">${teamMember.hourlyRate}/hr</span>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex shrink-0 flex-wrap gap-2">
                        <button type="button" onClick={() => editTeamMember(teamMember)} className={dashboardBtnRowEdit}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this team member?')) {
                              deleteTeamMember(teamMember.id);
                            }
                          }}
                          className={dashboardBtnRowDelete}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {teamsTab === 'subcontractors' && (
        <div className={dashboardCardClass}>
          <div className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
            <h3 className="text-base font-semibold text-slate-900">Your partners</h3>
            <p className="mt-1 text-sm text-slate-500">
              <span className="font-medium text-slate-700">{subcontractors.length}</span> on file — choose{' '}
              <span className="font-medium text-slate-700">Subcontractor</span> on labour lines to link spend.
            </p>
          </div>
          <div className="p-6">
            {subcontractors.length === 0 ? (
              <div className="py-12 text-center text-slate-500">
                <p className="font-medium text-slate-700">No partners yet</p>
                <p className="mt-1 text-sm">Add electricians, plumbers, or other subs you work with.</p>
                <button type="button" onClick={() => setShowAddSubcontractor(true)} className={`${dashboardBtnPrimary} mt-6`}>
                  Add your first partner
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {subcontractors.map((s) => (
                  <div
                    key={s.id}
                    className="rounded-2xl border border-slate-200/90 bg-white p-4 transition-all hover:border-slate-300 hover:shadow-sm"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="mb-2 flex flex-wrap items-center gap-2">
                          <h4 className="text-base font-semibold text-gray-900">{s.name}</h4>
                          {s.trade && (
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900">
                              {s.trade}
                            </span>
                          )}
                        </div>
                        {s.company && <p className="mb-2 text-sm text-gray-600">{s.company}</p>}
                        <div className="space-y-0.5 text-sm text-gray-600">
                          {s.phone && (
                            <div>
                              <span className="font-medium text-gray-700">Phone:</span> {s.phone}
                            </div>
                          )}
                          {s.email && (
                            <div>
                              <span className="font-medium text-gray-700">Email:</span> {s.email}
                            </div>
                          )}
                          {s.notes && <p className="mt-2 text-sm leading-snug text-gray-500">{s.notes}</p>}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-wrap gap-2">
                        <button type="button" onClick={() => editSubcontractor(s)} className={dashboardBtnRowEdit}>
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                            />
                          </svg>
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Remove this subcontractor / partner from your list?')) {
                              deleteSubcontractor(s.id);
                            }
                          }}
                          className={dashboardBtnRowDelete}
                        >
                          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showAddTeamMember &&
        ReactDOM.createPortal(
          <>
            <div className={dashboardModalBackdrop} aria-hidden />
            <div className={dashboardModalWrap}>
              <div className={dashboardModalPanelMd} role="dialog" aria-modal="true" aria-labelledby="team-member-form-title">
                <div className={dashboardModalHeader}>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Teams</p>
                    <h3 id="team-member-form-title" className="text-lg font-semibold text-slate-900">
                      {editingTeamMember ? 'Edit team member' : 'New team member'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTeamMember(false);
                      setEditingTeamMember(null);
                      setTeamMemberForm({ name: '', role: '', phone: '', email: '', hourlyRate: '' });
                    }}
                    className={dashboardModalCloseBtn}
                    title="Close"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    devClickDebug('TeamsSection: team member form onSubmit', {
                      hasName: !!teamMemberForm?.name,
                      hasRole: !!teamMemberForm?.role,
                    });
                    saveTeamMember(teamMemberForm);
                  }}
                  className="flex flex-1 flex-col overflow-hidden"
                >
                  <div className={`${dashboardModalBody} space-y-4`}>
                    <div>
                      <label className={dashboardLabelClass}>Name *</label>
                      <input
                        type="text"
                        value={teamMemberForm.name}
                        onChange={(e) => handleTeamMemberFormChange('name', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Role *</label>
                      <input
                        type="text"
                        value={teamMemberForm.role}
                        onChange={(e) => handleTeamMemberFormChange('role', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="e.g. Carpenter, Electrician"
                        required
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Phone</label>
                      <input
                        type="tel"
                        value={teamMemberForm.phone}
                        onChange={(e) => handleTeamMemberFormChange('phone', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Email</label>
                      <input
                        type="email"
                        value={teamMemberForm.email}
                        onChange={(e) => handleTeamMemberFormChange('email', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="Email address"
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Hourly rate ($)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={teamMemberForm.hourlyRate}
                        onChange={(e) => handleTeamMemberFormChange('hourlyRate', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className={dashboardModalFooter}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddTeamMember(false);
                        setEditingTeamMember(null);
                        setTeamMemberForm({ name: '', role: '', phone: '', email: '', hourlyRate: '' });
                      }}
                      className={dashboardBtnModalCancel}
                    >
                      Cancel
                    </button>
                    <button type="submit" className={dashboardBtnPrimary}>
                      {editingTeamMember ? 'Save changes' : 'Add member'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>,
          document.body
        )}

      {showAddSubcontractor &&
        ReactDOM.createPortal(
          <>
            <div className={dashboardModalBackdrop} aria-hidden />
            <div className={dashboardModalWrap}>
              <div className={dashboardModalPanelMd} role="dialog" aria-modal="true" aria-labelledby="subcontractor-form-title">
                <div className={dashboardModalHeader}>
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Subcontractors</p>
                    <h3 id="subcontractor-form-title" className="text-lg font-semibold text-slate-900">
                      {editingSubcontractor ? 'Edit partner' : 'New partner'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSubcontractor(false);
                      setEditingSubcontractor(null);
                      setSubcontractorForm(emptySubForm());
                    }}
                    className={dashboardModalCloseBtn}
                    title="Close"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    saveSubcontractor(subcontractorForm);
                  }}
                  className="flex flex-1 flex-col overflow-hidden"
                >
                  <div className={`${dashboardModalBody} space-y-4`}>
                    <div>
                      <label className={dashboardLabelClass}>Name *</label>
                      <input
                        type="text"
                        value={subcontractorForm.name}
                        onChange={(e) => handleSubcontractorFormChange('name', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="Contact or business name"
                        required
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Company</label>
                      <input
                        type="text"
                        value={subcontractorForm.company}
                        onChange={(e) => handleSubcontractorFormChange('company', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="Optional"
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Trade / role</label>
                      <input
                        type="text"
                        value={subcontractorForm.trade}
                        onChange={(e) => handleSubcontractorFormChange('trade', e.target.value)}
                        className={dashboardInputClass}
                        placeholder="e.g. Electrical, Plumbing"
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Phone</label>
                      <input
                        type="tel"
                        value={subcontractorForm.phone}
                        onChange={(e) => handleSubcontractorFormChange('phone', e.target.value)}
                        className={dashboardInputClass}
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Email</label>
                      <input
                        type="email"
                        value={subcontractorForm.email}
                        onChange={(e) => handleSubcontractorFormChange('email', e.target.value)}
                        className={dashboardInputClass}
                      />
                    </div>
                    <div>
                      <label className={dashboardLabelClass}>Notes</label>
                      <textarea
                        value={subcontractorForm.notes}
                        onChange={(e) => handleSubcontractorFormChange('notes', e.target.value)}
                        rows={3}
                        className={dashboardTextareaClass}
                        placeholder="License #, billing preferences…"
                      />
                    </div>
                  </div>
                  <div className={dashboardModalFooter}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddSubcontractor(false);
                        setEditingSubcontractor(null);
                        setSubcontractorForm(emptySubForm());
                      }}
                      className={dashboardBtnModalCancel}
                    >
                      Cancel
                    </button>
                    <button type="submit" className={dashboardBtnPrimary}>
                      {editingSubcontractor ? 'Save changes' : 'Save partner'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

export default TeamsSection;
